import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppIcon from '../components/AppIcon';
import { Leaf } from 'lucide-react';
import { getErrorMessage } from '../api/errors';
import ConsentCheckbox, { LegalLink } from '../components/ConsentCheckbox';
import { useAuth } from '../context/AuthContext';
import { savePendingVerifyEmail } from '../utils/emailVerification';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [kvkkRead, setKvkkRead] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    if (!termsAccepted || !kvkkRead) {
      setError('Devam etmek için zorunlu sözleşme onaylarını işaretlemelisiniz.');
      return;
    }

    setLoading(true);

    try {
      const result = await register(name, email, password, {
        termsAccepted,
        kvkkRead,
        marketingAccepted,
      });

      if (!result.requiresEmailVerification) {
        setError('Kayıt tamamlandı ancak e-posta doğrulama adımı beklenmiyor. Giriş yapmayı deneyin.');
        return;
      }

      savePendingVerifyEmail(result.user.email);
      navigate('/auth/verify-email-pending', {
        state: { email: result.user.email, message: result.message },
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Kayıt oluşturulamadı.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon">
            <AppIcon icon={Leaf} size={40} />
          </span>
          <h1>Kayıt Ol</h1>
          <p>Yeni hesap oluşturun</p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Ad Soyad</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Adınız Soyadınız"
              required
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">E-posta</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@email.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Şifre</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="En az 6 karakter"
              required
              autoComplete="new-password"
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Şifre Tekrar</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Şifrenizi tekrar girin"
              required
              autoComplete="new-password"
              minLength={6}
            />
          </div>

          <div className="consent-group">
            <ConsentCheckbox
              id="termsAccepted"
              checked={termsAccepted}
              onChange={setTermsAccepted}
              required
            >
              <LegalLink to="/yasal/kullanim-sozlesmesi">Web Sitesi Kullanım Sözleşmesi</LegalLink>
              &apos;ni okudum ve kabul ediyorum.
            </ConsentCheckbox>

            <ConsentCheckbox id="kvkkRead" checked={kvkkRead} onChange={setKvkkRead} required>
              <LegalLink to="/yasal/kvkk-aydinlatma-metni">KVKK Aydınlatma Metni</LegalLink>
              &apos;ni okudum.
            </ConsentCheckbox>

            <ConsentCheckbox
              id="marketingAccepted"
              checked={marketingAccepted}
              onChange={setMarketingAccepted}
            >
              Kampanya ve tanıtım içerikli ticari elektronik ileti almak istiyorum.
            </ConsentCheckbox>
          </div>

          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading}>
            {loading ? 'Kayıt oluşturuluyor...' : 'Kayıt Ol'}
          </button>
        </form>

        <p className="auth-footer">
          Zaten hesabınız var mı? <Link to="/login">Giriş Yap</Link>
        </p>
      </div>
    </div>
  );
}
